import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AudioFile } from '../types';

interface FavoritesContextType {
  favorites: AudioFile[];
  isFavorite: (audioId: number) => boolean;
  addToFavorites: (audio: AudioFile) => void;
  removeFromFavorites: (audioId: number) => void;
  toggleFavorite: (audio: AudioFile) => void;
  clearFavorites: () => void;
  getFavoritesCount: () => number;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

interface FavoritesProviderProps {
  children: React.ReactNode;
}

const FAVORITES_STORAGE_KEY = 'naatcity-favorites';

// Test localStorage availability
const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = '__localStorage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

const saveFavoritesToStorage = (favorites: AudioFile[]) => {
  if (!isLocalStorageAvailable()) {
    console.warn('localStorage is not available');
    return;
  }
  
  try {
    const favoritesData = JSON.stringify(favorites);
    localStorage.setItem(FAVORITES_STORAGE_KEY, favoritesData);
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`Saved ${favorites.length} favorites to localStorage`);
    }
  } catch (error) {
    console.error('Failed to save favorites to localStorage:', error);
  }
};

const loadFavoritesFromStorage = (): AudioFile[] => {
  if (!isLocalStorageAvailable()) {
    console.warn('localStorage is not available');
    return [];
  }
  
  try {
    const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
    if (!stored) {
      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.log('No favorites found in localStorage');
      }
      return [];
    }
    const parsed = JSON.parse(stored);
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`Loaded ${parsed.length} favorites from localStorage`);
    }
    return parsed;
  } catch (error) {
    // Failed to load favorites from localStorage - clear corrupted data
    try {
      localStorage.removeItem(FAVORITES_STORAGE_KEY);
    } catch {
      // Ignore cleanup errors
    }
    return [];
  }
};

export const FavoritesProvider: React.FC<FavoritesProviderProps> = ({ children }) => {
  const [favorites, setFavorites] = useState<AudioFile[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load favorites from localStorage on mount
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('FavoritesProvider: Loading favorites from storage...');
    }
    const storedFavorites = loadFavoritesFromStorage();
    setFavorites(storedFavorites);
    setIsInitialized(true);
  }, []);

  // Save favorites to localStorage whenever favorites change (but not on initial load)
  useEffect(() => {
    if (isInitialized) {
      if (process.env.NODE_ENV === 'development') {
        console.log('FavoritesProvider: Saving favorites to storage...');
      }
      saveFavoritesToStorage(favorites);
    }
  }, [favorites, isInitialized]);

  const isFavorite = useCallback((audioId: number): boolean => {
    return favorites.some(fav => fav.id === audioId);
  }, [favorites]);

  const addToFavorites = useCallback((audio: AudioFile) => {
    setFavorites(prevFavorites => {
      // Check if already exists
      if (prevFavorites.some(fav => fav.id === audio.id)) {
        return prevFavorites;
      }
      // Add to beginning of array so newest favorites appear first
      return [audio, ...prevFavorites];
    });
  }, []);

  const removeFromFavorites = useCallback((audioId: number) => {
    setFavorites(prevFavorites => 
      prevFavorites.filter(fav => fav.id !== audioId)
    );
  }, []);

  const toggleFavorite = useCallback((audio: AudioFile) => {
    setFavorites(prevFavorites => {
      const isCurrentlyFavorite = prevFavorites.some(fav => fav.id === audio.id);
      
      if (isCurrentlyFavorite) {
        // Remove from favorites
        return prevFavorites.filter(fav => fav.id !== audio.id);
      } else {
        // Add to favorites at the beginning
        return [audio, ...prevFavorites];
      }
    });
  }, []);

  const clearFavorites = useCallback(() => {
    setFavorites([]);
  }, []);

  const getFavoritesCount = useCallback((): number => {
    return favorites.length;
  }, [favorites]);

  const contextValue: FavoritesContextType = {
    favorites,
    isFavorite,
    addToFavorites,
    removeFromFavorites,
    toggleFavorite,
    clearFavorites,
    getFavoritesCount,
  };

  return (
    <FavoritesContext.Provider value={contextValue}>
      {children}
    </FavoritesContext.Provider>
  );
};

export default FavoritesProvider;
